import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'reactstrap';
import { getProjectDisplayName, getProjectId, isSameProject } from 'utils/smartContent/useAssignTextToProject';
import './SmartContentPopintrad.css';

const PROJECT_AVATAR_COLORS = [
  '#6458F1',
  '#EC8B66',
  '#1F9D55',
  '#3B82C4',
  '#C4547F',
  '#8B5CF6',
  '#F59E0B',
];

const getProjectAvatarColor = (project, index) => {
  const id = getProjectId(project);
  if (id) {
    return PROJECT_AVATAR_COLORS[Math.abs(Number(id)) % PROJECT_AVATAR_COLORS.length];
  }
  return PROJECT_AVATAR_COLORS[index % PROJECT_AVATAR_COLORS.length];
};

const FolderIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 7a2 2 0 012-2h4l2 3h8a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
  </svg>
);

const FolderPlusIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 7a2 2 0 012-2h4l2 3h8a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <path d="M12 11v6M9 14h6" />
  </svg>
);

const AssignTextToProjectPopin = ({
  isOpen,
  onClose,
  projects = [],
  loadingProjects = false,
  isSaving = false,
  errorKey = '',
  onSave,
  onCreateProject,
  keyword = '',
  title,
  saveButtonLabel,
}) => {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedProject(null);
      setSearchValue('');
      setSubmitError('');
    }
  }, [isOpen]);

  const resolveErrorMessage = (key) => {
    if (key === 'assign_project_load_error') {
      return t('Impossible de charger la liste des projets. Veuillez réessayer.');
    }
    if (key === 'assign_project_save_error') {
      return t('Une erreur est survenue lors de l\'ajout au projet. Veuillez réessayer.');
    }
    if (key === 'assign_project_selection_error') {
      return t('Sélectionnez un projet.');
    }
    return '';
  };

  const filteredProjects = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return projects;

    return projects.filter((project) => {
      const name = getProjectDisplayName(project).toLowerCase();
      const id = String(project?.id ?? '').toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [projects, searchValue]);

  const handleSave = async () => {
    setSubmitError('');

    if (!selectedProject) {
      setSubmitError(t('Sélectionnez un projet.'));
      return;
    }

    if (typeof onSave === 'function') {
      const success = await onSave(selectedProject);
      if (!success) {
        setSubmitError(resolveErrorMessage('assign_project_save_error'));
      }
    }
  };

  const keywordLabel = String(keyword || '').trim();
  const externalError = resolveErrorMessage(errorKey);
  const displayError = externalError || submitError;
  const canSave = Boolean(selectedProject) && !isSaving && !loadingProjects;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="assign-text-to-project-popin"
      backdropClassName="assign-text-to-project-backdrop"
    >
      <div className="assign-text-to-project-modal">
        <div className="assign-text-to-project-header">
          <div className="assign-text-to-project-header-top">
            <div className="assign-text-to-project-title-block">
              <div className="assign-text-to-project-icon-wrap" aria-hidden="true">
                <FolderIcon />
              </div>
              <div>
                <h2 className="assign-text-to-project-title">
                  {title || t('Lier à un projet')}
                </h2>
                {keywordLabel ? (
                  <p className="assign-text-to-project-subtitle">
                    {t('Le texte')}
                    {' '}
                    <strong>
                      «
                      {keywordLabel}
                      »
                    </strong>
                    {' '}
                    {t('sera sauvegardé sous le projet sélectionné.')}
                  </p>
                ) : (
                  <p className="assign-text-to-project-subtitle">
                    {t('Sélectionnez le projet auquel lier ce texte.')}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              className="assign-text-to-project-close"
              onClick={onClose}
              aria-label={t('Fermer')}
            >
              ✕
            </button>
          </div>

          {!loadingProjects && projects.length > 0 ? (
            <div className="assign-text-to-project-search">
              <span className="assign-text-to-project-search-icon" aria-hidden="true">
                <SearchIcon />
              </span>
              <input
                type="text"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder={t('Rechercher un projet…')}
                className="assign-text-to-project-search-input"
                aria-label={t('Rechercher un projet…')}
              />
            </div>
          ) : null}
        </div>

        <form
          className="assign-text-to-project-form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSave();
          }}
        >
          <div className="assign-text-to-project-body tx-scroll">
            {displayError ? (
              <p className="assign-text-to-project-error" role="alert">
                {displayError}
              </p>
            ) : null}

            {loadingProjects ? (
              <p className="assign-text-to-project-state">{t('Chargement...')}</p>
            ) : projects.length === 0 ? (
              <div className="assign-text-to-project-empty">
                <div className="assign-text-to-project-empty-icon-wrap" aria-hidden="true">
                  <FolderPlusIcon />
                </div>
                <h3 className="assign-text-to-project-empty-title">
                  {t('Aucun projet pour ce site')}
                </h3>
                <p className="assign-text-to-project-empty-desc">
                  {t('Créez un premier projet pour commencer à y regrouper vos textes sauvegardés.')}
                </p>
                {typeof onCreateProject === 'function' ? (
                  <button
                    type="button"
                    className="assign-text-to-project-create-btn"
                    onClick={onCreateProject}
                  >
                    +
                    {' '}
                    {t('Créer un projet')}
                  </button>
                ) : null}
              </div>
            ) : (
              <>
                <div className="assign-text-to-project-section-header">
                  <span className="assign-text-to-project-section-label">
                    {t('VOS PROJETS')}
                  </span>
                  {typeof onCreateProject === 'function' ? (
                    <button
                      type="button"
                      className="assign-text-to-project-new-link"
                      onClick={onCreateProject}
                    >
                      +
                      {' '}
                      {t('Nouveau projet')}
                    </button>
                  ) : null}
                </div>

                {filteredProjects.length === 0 ? (
                  <p className="assign-text-to-project-state">
                    {t('Aucun projet ne correspond.')}
                  </p>
                ) : (
                  <div
                    className="assign-text-to-project-list"
                    role="listbox"
                    aria-label={t('Sélectionnez un projet')}
                  >
                    {filteredProjects.map((project, index) => {
                      const isSelected = isSameProject(selectedProject, project);
                      const name = getProjectDisplayName(project) || t('Sans titre');
                      const initial = name.charAt(0).toUpperCase() || '?';
                      const avatarColor = getProjectAvatarColor(project, index);
                      const projectKey = `${getProjectDisplayName(project)}-${index}`;

                      return (
                        <button
                          key={projectKey}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={`assign-text-to-project-card${isSelected ? ' is-selected' : ''}`}
                          onClick={() => setSelectedProject(project)}
                        >
                          <span
                            className="assign-text-to-project-avatar"
                            style={{ backgroundColor: avatarColor }}
                            aria-hidden="true"
                          >
                            {initial}
                          </span>
                          <span className="assign-text-to-project-card-content">
                            <span className="assign-text-to-project-card-label">{name}</span>
                          </span>
                          {isSelected ? (
                            <span className="assign-text-to-project-check" aria-hidden="true">
                              <CheckIcon />
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="assign-text-to-project-footer">
            <button
              type="button"
              className="assign-text-to-project-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              {t('Annuler')}
            </button>
            <button
              type="submit"
              className="assign-text-to-project-save"
              disabled={!canSave}
            >
              {isSaving ? t('Envoi en cours...') : saveButtonLabel || t('Sauvegarder')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AssignTextToProjectPopin;
